import React, { useEffect, useState } from 'react';
import { supabase, supabaseAdmin } from '../lib/supabase';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useLogs } from '../hooks/useLogs';
import { useAuthContext } from '../contexts/AuthContext';
import { logAction, getCurrentAdmin } from '../services/logger';
import { LogRow } from '../components/LogRow';
import { StatusBadge } from '../components/StatusBadge';
import { Users, Trophy, CheckCircle, ThumbsUp, UserPlus, Shield, Trash2, Check, Beer, Radio, CalendarDays, TrendingUp, UserCheck } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';

interface Stats {
  bars: number;
  barsOnline: number;
  matches: number;
  matchesCDM: number;
  diffusions: number;
  confirmedTotal: number;
  confirmedToday: number;
  validationsToday: number;
  checkinsTotal: number;
  uniqueUsers: number;
  tonightMatches: number;
}

export function DashboardPage() {
  const { isSuperAdmin, admin } = useAuthContext();
  const [stats, setStats] = useState<Stats>({
    bars: 0, barsOnline: 0, matches: 0, matchesCDM: 0,
    diffusions: 0, confirmedTotal: 0, confirmedToday: 0,
    validationsToday: 0, checkinsTotal: 0, uniqueUsers: 0, tonightMatches: 0,
  });
  const [pieData, setPieData] = useState<any[]>([]);
  const [tonightDiffusions, setTonightDiffusions] = useState<any[]>([]);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [adminList, setAdminList] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('admin');
  const [inviting, setInviting] = useState(false);
  const { logs } = useLogs({ dateRange: 'week' });

  useEffect(() => {
    loadStats();
    if (isSuperAdmin) loadAdmins();
  }, [isSuperAdmin]);

  const loadStats = async () => {
    const today = new Date().toISOString().split('T')[0];

    const [barsRes, barsOnlineRes, matchesRes, matchesCDMRes, diffRes, confTotalRes, confTodayRes, tonightRes, validRes, checkinsRes, usersRes] = await Promise.all([
      supabaseAdmin.from('bars').select('id', { count: 'exact', head: true }),
      supabaseAdmin.from('bars').select('id', { count: 'exact', head: true }).eq('is_online', true),
      supabaseAdmin.from('matches').select('id', { count: 'exact', head: true }).gte('match_date', today),
      supabaseAdmin.from('matches').select('id', { count: 'exact', head: true }).eq('competition', 'Coupe du Monde FIFA 2026'),
      supabaseAdmin.from('diffusions').select('status'),
      supabaseAdmin.from('diffusions').select('id', { count: 'exact', head: true }).eq('status', 'confirmed'),
      supabaseAdmin.from('diffusions').select('id', { count: 'exact', head: true }).eq('status', 'confirmed').gte('confirmed_at', today + 'T00:00:00'),
      supabaseAdmin.from('matches').select('id, home_team, away_team, match_time, competition').eq('match_date', today).order('match_time'),
      supabaseAdmin.from('bar_validations').select('id', { count: 'exact', head: true }).gte('created_at', today + 'T00:00:00'),
      supabaseAdmin.from('checkins').select('id', { count: 'exact', head: true }),
      supabaseAdmin.from('checkins').select('user_id'),
    ]);

    const uniqueUserIds = new Set((usersRes.data || []).map((d: any) => d.user_id));

    setStats({
      bars: barsRes.count || 0,
      barsOnline: barsOnlineRes.count || 0,
      matches: matchesRes.count || 0,
      matchesCDM: matchesCDMRes.count || 0,
      diffusions: (diffRes.data || []).length,
      confirmedTotal: confTotalRes.count || 0,
      confirmedToday: confTodayRes.count || 0,
      validationsToday: validRes.count || 0,
      checkinsTotal: checkinsRes.count || 0,
      uniqueUsers: uniqueUserIds.size,
      tonightMatches: (tonightRes.data || []).length,
    });

    // Pie chart
    const allDiffs = diffRes.data || [];
    setPieData([
      { name: 'Non vérifié', value: allDiffs.filter((d: any) => d.status === 'unconfirmed').length, color: '#C53030' },
      { name: 'Probable', value: allDiffs.filter((d: any) => d.status === 'probable').length, color: '#C67A00' },
      { name: 'Confirmé', value: allDiffs.filter((d: any) => d.status === 'confirmed').length, color: '#1B7A1B' },
    ]);

    // Tonight
    if (tonightRes.data?.length) {
      const diffs = await supabaseAdmin.from('diffusions')
        .select('id, match_id, status, bars(name, probability)')
        .in('match_id', tonightRes.data.map((m: any) => m.id));
      setTonightDiffusions(tonightRes.data.map((m: any) => ({
        ...m,
        diffusions: (diffs.data || []).filter((d: any) => d.match_id === m.id),
      })));
    }

    // Recent active users (from checkins)
    const { data: recentCheckins } = await supabaseAdmin.from('checkins')
      .select('user_id, bar_id, created_at, bars(name)')
      .order('created_at', { ascending: false })
      .limit(8);
    setRecentUsers(recentCheckins || []);
  };

  const confirmHighProb = async (matchId: string) => {
    const match = tonightDiffusions.find((m: any) => m.id === matchId);
    if (!match) return;
    const highProb = match.diffusions.filter((d: any) => (d.bars?.probability ?? 0) >= 80 && d.status !== 'confirmed');
    if (highProb.length === 0) { toast('Aucune diffusion à confirmer'); return; }
    const adminData = getCurrentAdmin();
    for (const d of highProb) {
      await supabaseAdmin.from('diffusions').update({ status: 'confirmed', confirmed_at: new Date().toISOString(), confirmed_by: 'admin' }).eq('id', d.id);
    }
    await logAction('confirm_diffusion', 'diffusion', null, null,
      { count: highProb.length, match: `${match.home_team} vs ${match.away_team}` },
      `${adminData?.name} a confirmé ${highProb.length} diffusions pour ${match.home_team} vs ${match.away_team}`);
    toast.success(`${highProb.length} diffusions confirmées`);
    loadStats();
  };

  const loadAdmins = async () => {
    const { data } = await supabaseAdmin.from('admin_users').select('*').order('created_at');
    setAdminList(data || []);
  };

  const handleInviteAdmin = async () => {
    if (!inviteEmail || !inviteName) { toast.error('Email et nom requis'); return; }
    setInviting(true);
    try {
      const { error } = await supabaseAdmin.from('admin_users').insert({ email: inviteEmail.trim().toLowerCase(), name: inviteName.trim(), role: inviteRole });
      if (error) throw error;
      await logAction('invite_admin', 'admin', null, null, { email: inviteEmail, name: inviteName, role: inviteRole },
        `${admin?.name} a invité ${inviteName} comme ${inviteRole}`);
      toast.success(`${inviteName} ajouté`);
      setInviteEmail(''); setInviteName('');
      loadAdmins();
    } catch (e: any) { toast.error('Erreur: ' + e.message); }
    finally { setInviting(false); }
  };

  const handleRemoveAdmin = async (adminUser: any) => {
    if (adminUser.email === admin?.email) { toast.error('Tu ne peux pas te supprimer'); return; }
    if (!window.confirm(`Retirer ${adminUser.name} ?`)) return;
    await supabaseAdmin.from('admin_users').delete().eq('id', adminUser.id);
    await logAction('remove_admin', 'admin', adminUser.id, { email: adminUser.email }, null,
      `${admin?.name} a retiré ${adminUser.name}`);
    toast.success(`${adminUser.name} retiré`);
    loadAdmins();
  };

  return (
    <div>
      {/* ── Stats Row 1: Chiffres clés ── */}
      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Bars</div>
              <div className="stat-value">{stats.bars}</div>
              <div className="stat-sub">{stats.barsOnline} actifs</div>
            </div>
            <Beer size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Matchs</div>
              <div className="stat-value">{stats.matches}</div>
              <div className="stat-sub">{stats.matchesCDM} CDM 2026</div>
            </div>
            <Trophy size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Diffusions</div>
              <div className="stat-value" style={{ color: 'var(--green)' }}>{stats.confirmedTotal}</div>
              <div className="stat-sub">confirmées / {stats.diffusions} total</div>
            </div>
            <Radio size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Ce soir</div>
              <div className="stat-value" style={{ color: 'var(--orange)' }}>{stats.tonightMatches}</div>
              <div className="stat-sub">matchs aujourd'hui</div>
            </div>
            <CalendarDays size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
      </div>

      {/* ── Stats Row 2: Communauté ── */}
      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">ONpitchers</div>
              <div className="stat-value">{stats.uniqueUsers}</div>
              <div className="stat-sub">utilisateurs actifs</div>
            </div>
            <UserCheck size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Check-ins</div>
              <div className="stat-value">{stats.checkinsTotal}</div>
              <div className="stat-sub">total</div>
            </div>
            <TrendingUp size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Validations</div>
              <div className="stat-value" style={{ color: 'var(--orange)' }}>{stats.validationsToday}</div>
              <div className="stat-sub">aujourd'hui</div>
            </div>
            <ThumbsUp size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Confirmés</div>
              <div className="stat-value" style={{ color: 'var(--green)' }}>{stats.confirmedToday}</div>
              <div className="stat-sub">aujourd'hui</div>
            </div>
            <CheckCircle size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
      </div>

      {/* ── Row: Pie chart + Tonight ── */}
      <div className="dashboard-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="card">
          <h3 className="card-title">Diffusions par statut</h3>
          {pieData.some((d: any) => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={4} dataKey="value">
                  {pieData.map((entry: any, i: number) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #eee', borderRadius: '8px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="muted">Aucune donnée</p>}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', marginTop: '6px', flexWrap: 'wrap' }}>
            {pieData.map((d: any) => (
              <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--text-muted)' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: d.color, display: 'inline-block' }} />
                {d.name} ({d.value})
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="card-title">Matchs ce soir</h3>
          {tonightDiffusions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {tonightDiffusions.map((m: any) => {
                const confirmed = m.diffusions.filter((d: any) => d.status === 'confirmed').length;
                const total = m.diffusions.length;
                const highProb = m.diffusions.filter((d: any) => (d.bars?.probability ?? 0) >= 80 && d.status !== 'confirmed').length;
                return (
                  <div key={m.id} className="tonight-row">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong style={{ fontSize: '13px' }}>{m.home_team} vs {m.away_team}</strong>
                        <span style={{ fontSize: '11px', color: 'var(--text-dim)', marginLeft: '8px' }}>{m.match_time}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '11px', color: confirmed === total && total > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                          {confirmed}/{total}
                        </span>
                        {highProb > 0 && (
                          <button className="btn btn-green" style={{ fontSize: '10px', padding: '3px 8px' }} onClick={() => confirmHighProb(m.id)}>
                            <Check size={12} /> {highProb}
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                      {m.diffusions.map((d: any, i: number) => (
                        <span key={i} style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          <StatusBadge status={d.status} size="sm" />
                          <span style={{ marginLeft: '3px' }}>{d.bars?.name}</span>
                        </span>
                      ))}
                      {m.diffusions.length === 0 && <span className="muted" style={{ fontSize: '10px' }}>Aucune diffusion</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="muted">Aucun match ce soir</p>}
        </div>
      </div>

      {/* ── Row: Recent users + Recent actions ── */}
      <div className="dashboard-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
        <div className="card">
          <h3 className="card-title">Activité ONpitchers</h3>
          {recentUsers.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {recentUsers.map((c: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 600 }}>
                      {c.user_id?.substring(0, 8)}...
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      Check-in au {(c.bars as any)?.name || '?'}
                    </div>
                  </div>
                  <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                    {formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: fr })}
                  </span>
                </div>
              ))}
            </div>
          ) : <p className="muted">Aucune activité utilisateur</p>}
        </div>

        <div className="card">
          <h3 className="card-title">Dernières actions admin</h3>
          {logs.slice(0, 6).map(log => <LogRow key={log.id} log={log} />)}
          {logs.length === 0 && <p className="muted">Aucune action</p>}
        </div>
      </div>

      {/* ── Admin Management (super_admin) ── */}
      {isSuperAdmin && (
        <div className="card" style={{ marginTop: '16px' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Shield size={16} style={{ color: 'var(--accent)' }} /> Gestion des administrateurs
          </h3>
          <table className="data-table" style={{ marginBottom: '16px' }}>
            <thead><tr><th>Nom</th><th>Email</th><th>Rôle</th><th style={{ textAlign: 'right' }}>Action</th></tr></thead>
            <tbody>
              {adminList.map((a: any) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600 }}>{a.name}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{a.email}</td>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                      background: a.role === 'super_admin' ? 'var(--accent-dim)' : 'var(--blue-dim)',
                      color: a.role === 'super_admin' ? 'var(--accent)' : 'var(--blue)' }}>
                      {a.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {a.email !== admin?.email && (
                      <button className="btn-icon danger" onClick={() => handleRemoveAdmin(a)}><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '14px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <UserPlus size={16} style={{ color: 'var(--accent)' }} />
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Inviter un administrateur</span>
            </div>
            <div className="form-grid-3" style={{ alignItems: 'end' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Email</label>
                <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="hugo@email.com" />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Nom</label>
                <input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Hugo Dupont" />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select className="filter-select" value={inviteRole} onChange={e => setInviteRole(e.target.value)} style={{ flex: 1 }}>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
                <button className="btn btn-primary" onClick={handleInviteAdmin} disabled={inviting}>
                  {inviting ? '...' : 'Inviter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
